/**
 * Blog Comments Feature
 * Isolated: Handle blog comments functionality
 */
import { useState, useCallback, useEffect } from 'react';
import { blogApiService } from '../api';
import type { BlogComment } from '../api';
import { logger } from '@/shared/utils';
import toast from 'react-hot-toast';

export const useComments = (blogId: string) => {
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async (pageNum: number = 1) => {
    if (!blogId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await blogApiService.getComments(blogId, {
        page: pageNum,
        per_page: 20
      });
      
      // Handle both nested and flat response structures
      const commentsData = response.data?.comments || response.data || [];
      const totalCount = response.data?.total || (Array.isArray(commentsData) ? commentsData.length : 0);
      const hasMoreData = response.data?.has_more || false;
      
      // Map backend comment format to frontend format
      // Backend returns: { id, content, author_id, author_username, author_full_name, ... }
      // Frontend expects: { id, content, author: { id, username, full_name }, ... }
      const mapComment = (comment: any): BlogComment => {
        const authorUsername = comment.author_username || comment.author?.username || 'Anonymous';
        const authorFullName = comment.author_full_name || comment.author?.full_name || authorUsername;
        const authorId = comment.author_id || comment.author?.id || 0;
        
        return {
          id: comment.id,
          content: comment.content,
          author: {
            id: authorId,
            username: authorUsername,
            full_name: authorFullName,
            avatar_url: comment.author?.avatar_url
          },
          created_at: comment.created_at,
          updated_at: comment.updated_at,
          like_count: comment.like_count || 0,
          reply_count: comment.replies_count || comment.reply_count || 0,
          is_approved: comment.is_approved !== false,
          parent_comment_id: comment.parent_comment_id,
          replies: comment.replies ? comment.replies.map(mapComment) : undefined
        };
      };
      
      const mappedComments = Array.isArray(commentsData) 
        ? commentsData.map(mapComment)
        : [];
      
      if (pageNum === 1) {
        setComments(mappedComments);
      } else {
        setComments(prev => [...prev, ...mappedComments]);
      }
      setTotal(totalCount);
      setHasMore(hasMoreData);
      setPage(pageNum);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load comments';
      setError(errorMessage);
      logger.error('Failed to fetch comments', 'useComments', { blogId, error: err });
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  const createComment = useCallback(async (content: string, parentCommentId?: number) => {
    if (!blogId || !content.trim()) {
      toast.error('Comment cannot be empty');
      return null;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await blogApiService.createComment(blogId, {
        content: content.trim(),
        parent_comment_id: parentCommentId
      });

      if (response.success && response.data) {
        toast.success('Comment posted successfully');
        // Refresh comments
        await fetchComments(1);
        return response.data?.comment || response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to post comment');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to post comment';
      setError(errorMessage);
      toast.error(errorMessage);
      logger.error('Failed to create comment', 'useComments', { blogId, error: err });
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [blogId, fetchComments]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchComments(page + 1);
    }
  }, [loading, hasMore, page, fetchComments]);

  useEffect(() => {
    fetchComments(1);
  }, [fetchComments]);

  return {
    comments,
    loading,
    error,
    total,
    hasMore,
    submitting,
    createComment,
    loadMore,
    refreshComments: () => fetchComments(1)
  };
};

